import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";

const teamMembers = [
  {
    name: "Isabelle Nkemdirim",
    role: "Founder & Creative Director",
    roleF: "Fondatrice & Directrice Créative",
    image: "https://readdy.ai/api/search-image?query=elegant%20african%20woman%20entrepreneur%20smiling%20confidently%20in%20boutique%20setting%2C%20warm%20natural%20light%2C%20professional%20portrait%2C%20cream%20and%20rose%20tones%2C%20beautiful%20and%20sophisticated&width=300&height=350&seq=team1&orientation=portrait",
  },
  {
    name: "Marie-Claire Bello",
    role: "Head of Floral Design",
    roleF: "Responsable Design Floral",
    image: "https://readdy.ai/api/search-image?query=beautiful%20african%20woman%20florist%20arranging%20flowers%20in%20elegant%20boutique%2C%20warm%20natural%20light%2C%20professional%20portrait%2C%20cream%20and%20pink%20tones%2C%20creative%20and%20artistic&width=300&height=350&seq=team2&orientation=portrait",
  },
];

const values = [
  { icon: "ri-sparkling-line", key: "val1", color: "from-amber-500 to-amber-600" },
  { icon: "ri-heart-line", key: "val2", color: "from-amber-400 to-orange-500" },
  { icon: "ri-palette-line", key: "val3", color: "from-rose-500 to-rose-600" },
  { icon: "ri-hand-heart-line", key: "val4", color: "from-emerald-400 to-green-500" },
];

const stats = [
  { value: "2,500+", label: "Happy Customers", labelF: "Clients Satisfaits", icon: "ri-user-smile-line" },
  { value: "5,000+", label: "Gifts Delivered", labelF: "Cadeaux Livrés", icon: "ri-gift-line" },
  { value: "4.9/5", label: "Average Rating", labelF: "Note Moyenne", icon: "ri-star-line" },
  { value: "3+", label: "Years of Excellence", labelF: "Années d'Excellence", icon: "ri-award-line" },
];

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#f5ede0]">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 overflow-hidden">
        <div className="relative h-[420px] md:h-[520px]">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20premium%20gift%20boutique%20interior%20in%20Douala%20Cameroon%20with%20elegant%20flower%20arrangements%20candles%20and%20gift%20displays%2C%20warm%20golden%20light%2C%20luxurious%20and%20romantic%20atmosphere%2C%20cream%20and%20rose%20tones&width=1920&height=600&seq=about_hero&orientation=landscape"
            alt="About Creative Hub"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3d1f0a]/85 via-[#3d1f0a]/60 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
              <span className="inline-block text-amber-300 text-xs font-semibold uppercase tracking-widest mb-3">— Our Story —</span>
              <h1
                className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight max-w-xl"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("about_title")}
              </h1>
              <p className="text-white/75 text-lg max-w-lg">{t("about_subtitle")}</p>
            </div>
          </div>
        </div>
      </div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        {/* Stats */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)" }}
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-6 text-center border border-amber-100 hover:-translate-y-1 transition-transform duration-300"
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-amber-700 mx-auto mb-3">
                <i className={`${stat.icon} text-white text-xl`}></i>
              </div>
              <p className="text-3xl font-bold text-amber-800 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                {stat.value}
              </p>
              <p className="text-gray-600 text-sm">{isFr ? stat.labelF : stat.label}</p>
            </div>
          ))}
        </div>

        {/* Story Section */}
        <div
          className="grid lg:grid-cols-2 gap-16 items-center mb-20 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transitionDelay: "0.2s" }}
        >
          <div className="relative rounded-3xl overflow-hidden h-[450px]">
            <img
              src="https://readdy.ai/api/search-image?query=elegant%20gift%20boutique%20owner%20arranging%20beautiful%20flower%20bouquet%20in%20luxury%20shop%20interior%2C%20warm%20golden%20light%2C%20cream%20and%20rose%20tones%2C%20premium%20boutique%20aesthetic%2C%20Douala%20Cameroon&width=700&height=700&seq=about_story&orientation=squarish"
              alt="Our Story"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#3d1f0a]/30 to-transparent" />
          </div>
          <div>
            <span className="inline-block text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">— Who We Are —</span>
            <h2 className="text-4xl font-bold text-stone-800 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_story_title")}
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-6">{t("about_story")}</p>
            <div className="p-5 bg-gradient-to-br from-amber-50 to-[#f5ede0] rounded-2xl border border-amber-100">
              <h3 className="font-bold text-amber-800 mb-2">{t("about_mission_title")}</h3>
              <p className="text-gray-600 text-sm leading-relaxed italic">&ldquo;{t("about_mission")}&rdquo;</p>
            </div>
          </div>
        </div>

        {/* Values */}
        <div
          className="mb-20 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transitionDelay: "0.3s" }}
        >
          <div className="text-center mb-10">
            <span className="inline-block text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">— What Drives Us —</span>
            <h2 className="text-4xl font-bold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_values_title")}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {values.map((val, i) => (
              <div
                key={val.key}
                className="group text-center p-6 bg-white rounded-2xl border border-amber-100 hover:border-amber-200 hover:-translate-y-2 transition-all duration-300"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(30px)",
                  transition: `all 0.5s ease`,
                  transitionDelay: `${0.4 + i * 0.1}s`,
                }}
              >
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${val.color} mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${val.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="font-bold text-gray-800 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t(`about_${val.key}`)}
                </h3>
              </div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div
          className="transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transitionDelay: "0.4s" }}
        >
          <div className="text-center mb-10">
            <span className="inline-block text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">— The People Behind The Magic —</span>
            <h2 className="text-4xl font-bold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("about_team_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {teamMembers.map((member, i) => (
              <div
                key={i}
                className="group text-center"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(30px)",
                  transition: `all 0.5s ease`,
                  transitionDelay: `${0.5 + i * 0.1}s`,
                }}
              >
                <div className="relative rounded-2xl overflow-hidden h-56 mb-4 group-hover:-translate-y-1 transition-transform duration-300">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#3d1f0a]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <h3 className="font-bold text-gray-800 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {member.name}
                </h3>
                <p className="text-amber-700 text-xs mt-1">{isFr ? member.roleF : member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="relative py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=beautiful%20gift%20shop%20display%20with%20flowers%20and%20candles%20in%20warm%20golden%20light%2C%20premium%20boutique%2C%20elegant%20and%20romantic%20atmosphere%2C%20cream%20and%20rose%20tones&width=1920&height=400&seq=about_cta&orientation=landscape"
          alt="Visit Us"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3d1f0a]/85 to-[#5c3d1e]/75" />
        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {isFr ? "Visitez Notre Boutique" : "Visit Our Boutique"}
          </h2>
          <p className="text-white/75 text-lg mb-8">
            {isFr ? "1401 Melrose Place, Akwa, Douala, Cameroun" : "1401 Melrose Place, Akwa, Douala, Cameroon"}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/237678012159"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-400 hover:shadow-xl hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              WhatsApp Us
            </a>
            <a
              href="mailto:creativehub@gmail.com"
              className="flex items-center gap-2 px-8 py-4 bg-white text-amber-800 font-bold rounded-full hover:bg-amber-50 hover:shadow-xl hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line text-lg"></i>
              Email Us
            </a>
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}